import { translate, useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import downloadXLS from 'utils/downloadXLS';
import { formatNumber } from 'utils/formats';

import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import EmptyStateForTable from 'modules/_shared/components/EmptyStates/EmptyStateForTable';
import InputCheckbox, {
  InputCheckboxSize,
} from 'modules/_shared/components/InputCheckbox';
import InputNumber from 'modules/_shared/components/InputNumber';
import { useCanjeModal } from '../context/CanjeModalContext';

const DEFAULT_ROWS_PER_PAGE = 5;
const MIN_PARTICIPANT_SEARCH_LENGTH = 3;
const PARTICIPANTS_TABLE_COLSPAN = 9;
const PREVIEW_TABLE_SKELETON_ROWS = 5;

/* eslint-disable react/no-array-index-key */
const CanjeParticipantsTableSkeletonBody: FC<{ rows: number }> = ({ rows }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={`canje-preview-skeleton-row-${rowIndex}`} className="nk-tb-item">
        {Array.from({ length: PARTICIPANTS_TABLE_COLSPAN }).map(
          (__, colIndex) => (
            <td
              key={`canje-preview-skeleton-cell-${rowIndex}-${colIndex}`}
              className="nk-tb-col py-2 px-1"
            >
              <div className="placeholder-glow">
                <span
                  className={`placeholder d-block h-20px ${
                    colIndex === 0 ? 'w-75' : 'w-50'
                  }`}
                />
              </div>
            </td>
          )
        )}
      </tr>
    ))}
  </tbody>
);
/* eslint-enable react/no-array-index-key */

type AppliedCategoryBadge = { key: string; label: string };

const sortParticipantsByNewSocietySharesDesc = <
  T extends { newSocietyShares?: number; holderName?: string }
>(
  rows: T[]
): T[] =>
  [...rows].sort((a, b) => {
    const na = Number(a.newSocietyShares) || 0;
    const nb = Number(b.newSocietyShares) || 0;
    if (nb !== na) return nb - na;
    return (a.holderName || '').localeCompare(b.holderName || '', undefined, {
      sensitivity: 'base',
    });
  });

const getAppliedCategoryBadges = (
  allOpt: CustomAsyncSelectOption | null,
  peaksOpt: CustomAsyncSelectOption | null,
  postExchangeOrigin: number
): AppliedCategoryBadge[] => {
  const parts: AppliedCategoryBadge[] = [];
  const seen = new Set<string>();
  if (allOpt?.label != null && allOpt.value != null && allOpt.value !== '') {
    const k = String(allOpt.value);
    parts.push({ key: k, label: allOpt.label });
    seen.add(k);
  }
  if (
    peaksOpt?.label != null &&
    peaksOpt.value != null &&
    peaksOpt.value !== '' &&
    postExchangeOrigin >= 1
  ) {
    const v = String(peaksOpt.value);
    if (!seen.has(v)) {
      parts.push({ key: v, label: peaksOpt.label });
    }
  }
  return parts;
};

const CanjeMovementsView: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useCanjeModal();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [participantSearch, setParticipantSearch] = useState('');

  const participantSearchTrimmed = participantSearch.trim();

  const filteredParticipants = useMemo(() => {
    const q = participantSearchTrimmed.toLowerCase();
    const list =
      q.length < MIN_PARTICIPANT_SEARCH_LENGTH
        ? data.previewParticipants
        : data.previewParticipants.filter((row) =>
            (row.holderName || '').toLowerCase().includes(q)
          );
    return sortParticipantsByNewSocietySharesDesc(list);
  }, [data.previewParticipants, participantSearchTrimmed]);

  const paginatedParticipants = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredParticipants.slice(start, start + rowsPerPage);
  }, [filteredParticipants, page, rowsPerPage]);

  const participantsPaginationKey = useMemo(
    () =>
      data.previewParticipants.length > 0
        ? `${data.previewParticipants
            .map((r) => r.originPartnerId)
            .join(',')}|${participantSearch}`
        : 'empty',
    [data.previewParticipants, participantSearch]
  );

  useEffect(() => {
    setPage(1);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    setParticipantSearch('');
  }, [data.previewParticipants]);

  useEffect(() => {
    setPage(1);
  }, [participantSearch]);

  const fetchSocieties = useCallback(
    async (searchTerm: string) => {
      const term = searchTerm.toLowerCase();
      return data.targetSocietyOptions.filter((o) =>
        o.label.toLowerCase().includes(term)
      );
    },
    [data.targetSocietyOptions]
  );

  const fetchOriginCategories = useCallback(
    async (searchTerm: string) => {
      const term = searchTerm.toLowerCase();
      return data.originCategoryOptions.filter((o) =>
        o.label.toLowerCase().includes(term)
      );
    },
    [data.originCategoryOptions]
  );

  const fetchTargetCategories = useCallback(
    async (searchTerm: string) => {
      const term = searchTerm.toLowerCase();
      return data.targetCategoryOptions.filter((o) =>
        o.label.toLowerCase().includes(term)
      );
    },
    [data.targetCategoryOptions]
  );

  const targetClassLabel =
    data.selectedHoldingClassOption?.label?.split('|')[0]?.trim() || '—';

  const showParticipantsPreviewTable = useMemo(
    () =>
      !data.previewErrorKey &&
      Boolean(data.selectedOriginHoldingClass) &&
      data.ratioOld >= 1,
    [data.previewErrorKey, data.selectedOriginHoldingClass, data.ratioOld]
  );

  const allParticipantsSelected = useMemo(
    () =>
      data.previewParticipants.length > 0 &&
      data.selectedParticipantIds.length === data.previewParticipants.length,
    [data.previewParticipants, data.selectedParticipantIds]
  );

  const handleToggleAllParticipants = () => {
    if (allParticipantsSelected) {
      actions.handleDeselectAllParticipants();
      return;
    }
    actions.handleSelectAllParticipants();
  };

  const handleParticipantSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setParticipantSearch(e.target.value);
  };

  const handleDownloadCanjeTable = useCallback(() => {
    const participants = sortParticipantsByNewSocietySharesDesc(
      data.previewParticipants
    );
    if (!participants.length) return;

    const col = {
      holder: translate('CanjeMovementsHolder'),
      current: translate('CanjeCurrentShares'),
      postOrigin: translate('CanjePostOriginShares'),
      newTarget: translate('CanjeNewSocietyShares'),
      targetClass: translate('CanjeNewShareClassColumn'),
      catsOrigin: translate('CanjeTableCategoriesOrigin'),
      catsTarget: translate('CanjeTableCategoriesTarget'),
      encExchanged: translate('CanjeEncumberedSharesExchanged'),
      encTarget: translate('CanjeEncumberedSharesTarget'),
    };

    const classLabel =
      data.selectedHoldingClassOption?.label?.split('|')[0]?.trim() || '—';

    const rows = participants.map((row) => {
      const originBadges = getAppliedCategoryBadges(
        data.originCategoryAllOption,
        data.originCategoryPeaksOption,
        row.postExchangeOrigin
      );
      const targetBadges = getAppliedCategoryBadges(
        data.targetCategoryAllOption,
        data.targetCategoryPeaksOption,
        row.postExchangeOrigin
      );
      return {
        [col.holder]: row.holderName ?? '',
        [col.current]: row.currentShares,
        [col.postOrigin]: row.postExchangeOrigin,
        [col.newTarget]: row.newSocietyShares,
        [col.targetClass]: classLabel,
        [col.catsOrigin]: originBadges.map((b) => b.label).join('; '),
        [col.catsTarget]: targetBadges.map((b) => b.label).join('; '),
        [col.encExchanged]: row.encumberedSharesExchanged ?? 0,
        [col.encTarget]: row.encumberedSharesTarget ?? 0,
      };
    });

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadXLS(
      rows,
      `canje-participantes-${stamp}.xlsx`,
      translate('ExchangeOperation')
    );
  }, [
    data.previewParticipants,
    data.selectedHoldingClassOption,
    data.originCategoryAllOption,
    data.originCategoryPeaksOption,
    data.targetCategoryAllOption,
    data.targetCategoryPeaksOption,
  ]);

  return (
    <>
      <div className="form-group row align-items-stretch">
        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-origin-class">
              {t('OriginShareClassForExchange')} <MandatoryField />
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-origin-class"
                value={data.selectedOriginHoldingClassOption}
                onChange={actions.handleChangeOriginHoldingClass}
                onFetchData={actions.handleFetchOriginHoldingClassByTerm}
                placeholder={t('SearchClass')}
                minimumInputLength={0}
                defaultOptions={data.originHoldingClassOptions}
                noDataLabelKey="NoClassesHaveBeenFound"
                loadingLabelKey="LoadingClasses"
                errorLabelKey="ErrorLoadingClasses"
                isDisabled={data.isLoadingOriginHoldingClasses}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-origin-cat-all">
              {t('CanjeCategoryAllParticipantsOrigin')}
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-origin-cat-all"
                value={data.originCategoryAllOption}
                onChange={actions.handleChangeOriginCategoryAll}
                onFetchData={fetchOriginCategories}
                placeholder={t('CanjeCategoryOptionalPlaceholder')}
                minimumInputLength={0}
                defaultOptions={data.originCategoryOptions}
                noDataLabelKey="NoDataFound"
                loadingLabelKey="LoadingData"
                errorLabelKey="ErrorLoadingData"
                isDisabled={data.isLoadingOriginCategories}
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-origin-cat-peaks">
              {t('CanjeCategoryPeaksParticipantsOrigin')}
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-origin-cat-peaks"
                value={data.originCategoryPeaksOption}
                onChange={actions.handleChangeOriginCategoryPeaks}
                onFetchData={fetchOriginCategories}
                placeholder={t('CanjeCategoryOptionalPlaceholder')}
                minimumInputLength={0}
                defaultOptions={data.originCategoryOptions}
                noDataLabelKey="NoDataFound"
                loadingLabelKey="LoadingData"
                errorLabelKey="ErrorLoadingData"
                isDisabled={data.isLoadingOriginCategories}
                isClearable
              />
            </div>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('ExchangeSharesPerBatch')}</label>

        <span className="form-text-lg font-weight-bold badge badge-dim badge-primary mx-2 py-1 px-2">
          {formatNumber(data.ratioOld)} → {formatNumber(data.ratioNew)}
        </span>
        {!data.isLoadingPreview && data.previewParticipants.length > 0 && (
          <>
            <span className="form-text-lg font-weight-bold badge badge-dim badge-secondary mx-1 py-1 px-2">
              {t('CanjeParticipantsCountBadge', {
                count: formatNumber(data.previewParticipants.length),
              })}
            </span>
            <span className="form-text-lg font-weight-bold badge badge-dim badge-info mx-1 py-1 px-2">
              {t('CanjeSelectedCountBadge', {
                count: formatNumber(data.selectedParticipantIds.length),
              })}
            </span>
          </>
        )}
      </div>

      <div className="form-group">
        <InputNumber
          id="share-price"
          label={t('SharePrice')}
          value={data.sharePrice}
          onChange={actions.handleChangeSharePrice}
          iconRight="ni-sign-euro"
        />
      </div>

      <div className="form-group">
        <InputCheckbox
          id="only-partner-creation"
          label={t('PartnerCreationOnly')}
          checked={data.isPartnerCreationOnly}
          onChange={actions.handleToggleIsPartnerCreationOnly}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="canje-target-society">
          {t('TargetSocietyForExchange')} <MandatoryField />
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="canje-target-society"
            onChange={actions.handleTargetSocietyChange}
            onFetchData={fetchSocieties}
            value={data.targetSocietyOption}
            placeholder={t('TargetSocietyForExchange')}
            minimumInputLength={0}
            defaultOptions={data.targetSocietyOptions}
            noDataLabelKey="NoDataFound"
            loadingLabelKey="LoadingData"
            errorLabelKey="ErrorLoadingData"
            isDisabled={data.isLoadingSocieties}
          />
        </div>
      </div>

      <div className="form-group row align-items-stretch">
        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-target-class">
              {t('TargetShareClassForExchange')} <MandatoryField />
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-target-class"
                value={data.selectedHoldingClassOption}
                onChange={actions.handleChangeHoldingClass}
                onFetchData={actions.handleFetchHoldingClassByTerm}
                placeholder={t('SearchClass')}
                minimumInputLength={1}
                defaultOptions={data.holdingClassOptions}
                noDataLabelKey="NoClassesHaveBeenFound"
                loadingLabelKey="LoadingClasses"
                errorLabelKey="ErrorLoadingClasses"
                isDisabled={!data.targetSocietyId}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-target-cat-all">
              {t('CanjeCategoryAllParticipantsTarget')}
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-target-cat-all"
                value={data.targetCategoryAllOption}
                onChange={actions.handleChangeTargetCategoryAll}
                onFetchData={fetchTargetCategories}
                placeholder={t('CanjeCategoryOptionalPlaceholder')}
                minimumInputLength={0}
                defaultOptions={data.targetCategoryOptions}
                noDataLabelKey="NoDataFound"
                loadingLabelKey="LoadingData"
                errorLabelKey="ErrorLoadingData"
                isDisabled={
                  !data.targetSocietyId || data.isLoadingTargetCategories
                }
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 d-flex">
          <div className="form-group d-flex flex-column justify-content-end w-100">
            <label className="form-label" htmlFor="canje-target-cat-peaks">
              {t('CanjeCategoryPeaksParticipantsTarget')}
            </label>
            <div className="form-control-wrap">
              <CustomAsyncSelect
                id="canje-target-cat-peaks"
                value={data.targetCategoryPeaksOption}
                onChange={actions.handleChangeTargetCategoryPeaks}
                onFetchData={fetchTargetCategories}
                placeholder={t('CanjeCategoryOptionalPlaceholder')}
                minimumInputLength={0}
                defaultOptions={data.targetCategoryOptions}
                noDataLabelKey="NoDataFound"
                loadingLabelKey="LoadingData"
                errorLabelKey="ErrorLoadingData"
                isDisabled={
                  !data.targetSocietyId || data.isLoadingTargetCategories
                }
                isClearable
              />
            </div>
          </div>
        </div>
      </div>

      {data.previewErrorKey && (
        <p className="text-danger">{t(data.previewErrorKey)}</p>
      )}

      {showParticipantsPreviewTable && (
        <div className="mt-3">
          {!data.isLoadingPreview && data.previewParticipants.length > 0 && (
            <div className="form-group">
              <label className="form-label" htmlFor="canje-participants-search">
                {t('CanjeTableSearchByPartnerName')}
              </label>
              <div className="form-control-wrap">
                <div className="d-flex flex-wrap align-items-stretch">
                  <div
                    className="flex-grow-1 pr-sm-2 mb-2 mb-sm-0"
                    style={{ minWidth: 200 }}
                  >
                    <input
                      id="canje-participants-search"
                      type="search"
                      className="form-control h-100"
                      value={participantSearch}
                      onChange={handleParticipantSearchChange}
                      placeholder={t(
                        'CanjeTableSearchByPartnerNamePlaceholder'
                      )}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    icon="download"
                    className="align-self-stretch mr-sm-2 mb-2 mb-sm-0"
                    dimStyle
                    outlineStyle
                    onClick={handleDownloadCanjeTable}
                  >
                    {t('CanjeTableDownload')}
                  </Button>
                  <Button
                    icon={
                      allParticipantsSelected ? 'cross-circle' : 'check-circle'
                    }
                    className="align-self-stretch"
                    variant={ButtonVariants.PRIMARY}
                    dimStyle
                    outlineStyle
                    onClick={handleToggleAllParticipants}
                  >
                    {t(
                      allParticipantsSelected
                        ? 'CanjeDeselectAll'
                        : 'CanjeSelectAll'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="nk-tb-list nk-tb-ulist">
              <thead>
                <tr className="nk-tb-item nk-tb-head tb-tnx-head">
                  <th className="nk-tb-col">
                    <span>{t('CanjeMovementsHolder')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeCurrentShares')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjePostOriginShares')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeNewSocietyShares')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeNewShareClassColumn')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeTableCategoriesOrigin')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeTableCategoriesTarget')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeEncumberedSharesExchanged')}</span>
                  </th>
                  <th className="nk-tb-col">
                    <span>{t('CanjeEncumberedSharesTarget')}</span>
                  </th>
                </tr>
              </thead>
              {data.isLoadingPreview && (
                <CanjeParticipantsTableSkeletonBody
                  rows={PREVIEW_TABLE_SKELETON_ROWS}
                />
              )}
              {!data.isLoadingPreview &&
                data.previewParticipants.length === 0 && (
                  <EmptyStateForTable
                    mainMessage={t('CanjeNoParticipants')}
                    infoMessage=""
                    colspan={PARTICIPANTS_TABLE_COLSPAN}
                  />
                )}
              {!data.isLoadingPreview &&
                data.previewParticipants.length > 0 && (
                  <tbody>
                    {filteredParticipants.length === 0 && (
                      <tr className="nk-tb-item">
                        <td
                          className="nk-tb-col py-2"
                          colSpan={PARTICIPANTS_TABLE_COLSPAN}
                        >
                          <span className="text-soft">
                            {t('CanjeTableSearchNoResults')}
                          </span>
                        </td>
                      </tr>
                    )}
                    {paginatedParticipants.map((row) => {
                      const originCategoryBadges = getAppliedCategoryBadges(
                        data.originCategoryAllOption,
                        data.originCategoryPeaksOption,
                        row.postExchangeOrigin
                      );
                      const targetCategoryBadges = getAppliedCategoryBadges(
                        data.targetCategoryAllOption,
                        data.targetCategoryPeaksOption,
                        row.postExchangeOrigin
                      );
                      return (
                        <tr key={row.originPartnerId} className="nk-tb-item">
                          <td
                            className="nk-tb-col py-1 px-1 w-250px"
                            title={row.holderName}
                          >
                            <InputCheckbox
                              id={`canje-participant-${row.originPartnerId}`}
                              checked={data.selectedParticipantIds.includes(
                                row.originPartnerId
                              )}
                              label={row.holderName || ''}
                              onChange={() =>
                                actions.handleToggleParticipant(
                                  row.originPartnerId
                                )
                              }
                              size={InputCheckboxSize.SM}
                            />
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>{formatNumber(row.currentShares)}</span>
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>{formatNumber(row.postExchangeOrigin)}</span>
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>{formatNumber(row.newSocietyShares)}</span>
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>{targetClassLabel}</span>
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            {originCategoryBadges.length === 0 ? (
                              <span>—</span>
                            ) : (
                              originCategoryBadges.map(
                                ({ key: catKey, label }) => (
                                  <span
                                    key={`${row.originPartnerId}-o-${catKey}`}
                                    className="badge badge-light mr-1 mb-1"
                                    title={label}
                                  >
                                    {label}
                                  </span>
                                )
                              )
                            )}
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            {targetCategoryBadges.length === 0 ? (
                              <span>—</span>
                            ) : (
                              targetCategoryBadges.map(
                                ({ key: catKey, label }) => (
                                  <span
                                    key={`${row.originPartnerId}-t-${catKey}`}
                                    className="badge badge-gray mr-1 mb-1"
                                    title={label}
                                  >
                                    {label}
                                  </span>
                                )
                              )
                            )}
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>
                              {formatNumber(row.encumberedSharesExchanged ?? 0)}
                            </span>
                          </td>
                          <td className="nk-tb-col py-1 px-1">
                            <span>
                              {formatNumber(row.encumberedSharesTarget ?? 0)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
            </table>

            {!data.isLoadingPreview &&
              data.previewParticipants.length > 0 &&
              filteredParticipants.length > 0 && (
                <div className="w-100 overflow-x-hidden">
                  <TablePagination
                    key={participantsPaginationKey}
                    totalRows={filteredParticipants.length}
                    showRowsPerPageOptions={false}
                    defaultPage={1}
                    defaultRowsPerPage={rowsPerPage}
                    onChangePage={setPage}
                    onChangeRowsPerPage={(n) => {
                      setRowsPerPage(n);
                      setPage(1);
                    }}
                  />
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default CanjeMovementsView;
